import type { MarketAppWithTabs, TileBigNumber, WidgetV2 } from '@/features/Dashboard/types';

import type { DashboardEditDraft, TabDraft } from '../dashboardEdit.types';

/**
 * Deep clone WidgetV2 (app tab).
 */
export const cloneTab = (tab: WidgetV2): WidgetV2 => {
  return {
    ...tab,
    metadata: tab.metadata ? ({ ...tab.metadata } as any) : undefined
  };
};

/**
 * Deep clone MarketAppWithTabs to prevent reference sharing.
 */
export const cloneApp = (app: MarketAppWithTabs): MarketAppWithTabs => {
  return {
    ...app,
    tabs: app.tabs.map(tab => cloneTab(tab))
  };
};

/**
 * Deep clone TileBigNumber.
 */
export const cloneTile = (tile: TileBigNumber): TileBigNumber => {
  return {
    ...tile,
    symbol: {
      ...tile.symbol,
      key: { ...tile.symbol.key }
    }
  };
};

/**
 * Deep clone TabDraft.
 */
export const cloneTabDraft = (draft: TabDraft): TabDraft => {
  return {
    marketTabApps: draft.marketTabApps.map(app => cloneApp(app)),
    tiles: draft.tiles.map(tile => cloneTile(tile))
  };
};

/**
 * Clone array of apps.
 */
export const cloneApps = (apps: MarketAppWithTabs[]): MarketAppWithTabs[] => {
  return apps.map(app => cloneApp(app));
};

/**
 * Clone array of tiles.
 */
export const cloneTiles = (tiles: TileBigNumber[]): TileBigNumber[] => {
  return tiles.map(tile => cloneTile(tile));
};

/**
 * Deep clone dashboard edit draft.
 */
export const cloneDashboardDraft = (draft: DashboardEditDraft): DashboardEditDraft => {
  const clonedTabDrafts: Partial<Record<string, TabDraft>> = {};

  Object.entries(draft.tabDrafts).forEach(([tabId, tabDraft]) => {
    if (tabDraft) clonedTabDrafts[tabId] = cloneTabDraft(tabDraft);
  });

  return {
    draftTabs: draft.draftTabs.map(t => ({ ...t })),
    tabDrafts: clonedTabDrafts
  };
};
